// components/layout/Navbar.js
import React, { useState } from 'react';
import ThemeToggle from './ThemeToggle';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 70px;
  background: var(--background-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 1000;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;

  img {
    height: 40px;
    width: auto;
  }
`;

const NavLinks = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 70px;
    left: 0;
    right: 0;
    background: var(--background-primary);
    padding: ${props => props.isOpen ? '1rem 2rem' : '0 2rem'};
    max-height: ${props => props.isOpen ? '500px' : '0'};
    overflow: hidden;
    box-shadow: ${props => props.isOpen ? '0 4px 6px rgba(0,0,0,0.1)' : 'none'};
    gap: 1.5rem;
    align-items: center;
    z-index: 999;
    transition: all 0.3s ease-in-out;
    opacity: ${props => props.isOpen ? '1' : '0'};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  }
`;

const NavLink = styled.a`
  color: var(--text-primary);
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 0.9rem;

  &:hover {
    color: var(--accent-primary);
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 0;
    width: 100%;
    text-align: center;
    font-size: 1.1rem;
    border-bottom: 1px solid var(--border-color);

    &:last-of-type {
      border-bottom: none;
    }

    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    color: var(--accent-primary);
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
    transition: opacity 0.3s ease;
    opacity: ${props => props.isOpen ? '1' : '0'};
  }
`;

const DesktopNavLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleNavigation = (sectionId) => {
        // Close the mobile menu when a link is clicked
        setIsMenuOpen(false);

        if (location.pathname !== '/') {
            // If we're not on the home page, navigate to home first
            navigate('/');
            // Use setTimeout to ensure the navigation completes before trying to scroll
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        } else {
            // If we're already on the home page, just scroll
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    return (
        <Nav>
            <Logo>
                <NavLink onClick={() => handleNavigation('home')}>
                    <img src="/signage-logo.png" alt="Signage Logo" />
                </NavLink>
            </Logo>

            {/* Desktop Navigation */}
            <DesktopNavLinks>
                <NavLink onClick={() => handleNavigation('home')}>Home</NavLink>
                <NavLink onClick={() => handleNavigation('demo')}>Demo</NavLink>
                <NavLink onClick={() => handleNavigation('features')}>Features</NavLink>
                <NavLink onClick={() => handleNavigation('about')}>About Us</NavLink>
                <NavLink onClick={() => handleNavigation('pricing')}>Pricing</NavLink>
                <NavLink onClick={() => { setIsMenuOpen(false); navigate('/sponsor'); }}>Sponsor</NavLink>
                <NavLink onClick={() => handleNavigation('contact')}>Contact</NavLink>
                <ThemeToggle />
            </DesktopNavLinks>

            {/* Mobile Navigation */}
            <MobileMenuButton onClick={toggleMenu} aria-label="Toggle menu">
                <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
            </MobileMenuButton>

            <NavLinks isOpen={isMenuOpen}>
                <NavLink onClick={() => handleNavigation('home')}>Home</NavLink>
                <NavLink onClick={() => handleNavigation('demo')}>Demo</NavLink>
                <NavLink onClick={() => handleNavigation('features')}>Features</NavLink>
                <NavLink onClick={() => handleNavigation('about')}>About Us</NavLink>
                <NavLink onClick={() => handleNavigation('pricing')}>Pricing</NavLink>
                <NavLink onClick={() => { setIsMenuOpen(false); navigate('/sponsor'); }}>Sponsor</NavLink>
                <NavLink onClick={() => handleNavigation('contact')}>Contact</NavLink>
                <ThemeToggle />
            </NavLinks>

            {/* Background overlay for mobile */}
            {isMenuOpen && <Overlay isOpen={isMenuOpen} onClick={toggleMenu} />}
        </Nav>
    );
};

export default Navbar;
